import React from 'react';
import css from './ContractWizardModal.module.css';

export interface ContractStepCompensationProps {
  selectedEmploymentType: string;
  compensationType: 'hourly' | 'salary';
  setCompensationType: (type: 'hourly' | 'salary') => void;
  formValues: Record<string, any>;
  handleFieldChange: (key: string, value: any) => void;
  validationErrors: Record<string, string>;
  minijobWarning: string;
}

export function ContractStepCompensation({
  selectedEmploymentType,
  compensationType,
  setCompensationType,
  formValues,
  handleFieldChange,
  validationErrors,
  minijobWarning,
}: ContractStepCompensationProps) {
  const isMinijob =
    selectedEmploymentType?.toUpperCase() === 'MINIJOB';

  return (
    <>
      <h3 className={css.compensationHeading}>Vergütung</h3>

      {!isMinijob && (
        <div className={css.fieldGroup}>
          <label className={css.labelStyle}>Vergütungsart</label>
          <select
            value={compensationType}
            onChange={(e) =>
              setCompensationType(e.target.value as 'hourly' | 'salary')
            }
            className={css.inputStyle}
          >
            <option value="HOURLY">Stundenlohn</option>
            <option value="SALARY">Monatsgehalt</option>
          </select>
        </div>
      )}

      {compensationType === 'hourly' ? (
        <HourlyFields
          formValues={formValues}
          handleFieldChange={handleFieldChange}
          validationErrors={validationErrors}
          isMinijob={isMinijob}
          minijobWarning={minijobWarning}
        />
      ) : (
        <SalaryFields
          formValues={formValues}
          handleFieldChange={handleFieldChange}
          validationErrors={validationErrors}
        />
      )}
    </>
  );
}

function HourlyFields({
  formValues,
  handleFieldChange,
  validationErrors,
  isMinijob,
  minijobWarning,
}: {
  formValues: Record<string, any>;
  handleFieldChange: (key: string, value: any) => void;
  validationErrors: Record<string, string>;
  isMinijob: boolean;
  minijobWarning: string;
}) {
  return (
    <>
      <div className={css.fieldGroup}>
        <label className={css.labelStyle}>
          Stundenlohn (EUR) *
        </label>
        <input
          type="number"
          step="0.01"
          min="0"
          value={formValues['hourly_rate'] ?? ''}
          onChange={(e) =>
            handleFieldChange(
              'hourly_rate',
              e.target.value ? Number(e.target.value) : null
            )
          }
          className={css.inputStyle}
          style={
            validationErrors['hourly_rate']
              ? { border: '1px solid #ef4444' }
              : { border: '1px solid #e5e7eb' }
          }
          placeholder="z.B. 12.82"
        />
        {validationErrors['hourly_rate'] && (
          <div className={css.validationError}>
            {validationErrors['hourly_rate']}
          </div>
        )}
      </div>

      <div className={css.fieldGroup}>
        <label className={css.labelStyle}>
          Wochenstunden *
        </label>
        <input
          type="number"
          step="0.25"
          min="0"
          value={formValues['contract_hours_per_week'] ?? ''}
          onChange={(e) =>
            handleFieldChange(
              'contract_hours_per_week',
              e.target.value ? Number(e.target.value) : null
            )
          }
          className={css.inputStyle}
          style={
            validationErrors['contract_hours_per_week']
              ? { border: '1px solid #ef4444' }
              : { border: '1px solid #e5e7eb' }
          }
          placeholder="z.B. 10"
        />
        {validationErrors['contract_hours_per_week'] && (
          <div className={css.validationError}>
            {validationErrors['contract_hours_per_week']}
          </div>
        )}
        {isMinijob &&
          formValues['hourly_rate'] &&
          formValues['hourly_rate'] > 0 && (
            <div className={css.autoCalcNote}>
              ✓ Automatisch berechnet für 556 € Minijob-Grenze
            </div>
          )}
      </div>

      <div className={css.derivedHours}>
        Monatsstunden (abgeleitet):{' '}
        {formValues['monthly_hours']
          ? Number(formValues['monthly_hours']).toFixed(2)
          : '—'}
      </div>

      {isMinijob && minijobWarning && (
        <div className={css.warningBox}>{minijobWarning}</div>
      )}
    </>
  );
}

function SalaryFields({
  formValues,
  handleFieldChange,
  validationErrors,
}: {
  formValues: Record<string, any>;
  handleFieldChange: (key: string, value: any) => void;
  validationErrors: Record<string, string>;
}) {
  return (
    <>
      <div className={css.fieldGroup}>
        <label className={css.labelStyle}>
          Monatsgehalt (Cent) *
        </label>
        <input
          type="number"
          step="100"
          min="0"
          value={formValues['salary_monthly_cents'] ?? ''}
          onChange={(e) =>
            handleFieldChange(
              'salary_monthly_cents',
              e.target.value ? Number(e.target.value) : null
            )
          }
          className={css.inputStyle}
          style={
            validationErrors['salary_monthly_cents']
              ? { border: '1px solid #ef4444' }
              : { border: '1px solid #e5e7eb' }
          }
          placeholder="z.B. 300000 für 3.000 €"
        />
        {validationErrors['salary_monthly_cents'] && (
          <div className={css.validationError}>
            {validationErrors['salary_monthly_cents']}
          </div>
        )}
        <div className={css.centHint}>
          Bitte Betrag in Cent eingeben (z.B. 300000 für 3.000 €)
        </div>
      </div>

      <div className={css.fieldGroup}>
        <label className={css.labelStyle}>
          Monatsstunden *
        </label>
        <input
          type="number"
          step="0.25"
          min="0"
          value={formValues['monthly_hours'] ?? ''}
          onChange={(e) =>
            handleFieldChange(
              'monthly_hours',
              e.target.value ? Number(e.target.value) : null
            )
          }
          className={css.inputStyle}
          style={
            validationErrors['monthly_hours']
              ? { border: '1px solid #ef4444' }
              : { border: '1px solid #e5e7eb' }
          }
          placeholder="z.B. 86.67"
        />
        {validationErrors['monthly_hours'] && (
          <div className={css.validationError}>
            {validationErrors['monthly_hours']}
          </div>
        )}
      </div>

      <div className={css.derivedHours}>
        Wochenstunden (abgeleitet):{' '}
        {formValues['contract_hours_per_week']
          ? Number(formValues['contract_hours_per_week']).toFixed(2)
          : '—'}
      </div>
    </>
  );
}
